import { TbCheck } from "react-icons/tb";
import { useCarePlusPrice } from "../useCarePlusPrice";
import { Translate } from "../../ChoosePlanCarePlus/planContent";
import { CarePlusDosageOption } from "../../ChooseTreatmentCarePlus/types";
// Aliased: the `packLabel` prop below (the selected item's label) would shadow the helper.
import { packLabel as rowPackLabel } from "../../ChooseTreatmentCarePlusNew/helpers";

// "Preis- & Dosierungsübersicht": title, body, "Learn more", the dosage/price
// table (current dose ticked), pack note, and the orange savings-basis banner.
interface Props {
  t: Translate;
  medicationTitle: string;
  dosages: CarePlusDosageOption[];
  selectedProductItemId: number | null;
  onLearnMore: () => void;
  /** The savings-basis note accompanies a crossed-out price and only that. The page
   * passes whether the Monthly card or the One Time row currently shows one. */
  showSavingsNote: boolean;
  /** the pack note names the form — "Tabletten" for a tablet product (catalog
   * settings.show_medication_in_plan_price) or a pill pack, "Injektions-Pens" for a pen, otherwise
   * the unit word of the selected item's own pack label (see packLabel). */
  isTabletProduct: boolean;
  /** The selected item's localized pack label ("42 St.", "1 Pen"), from product_item_translations. */
  packLabel: string;
}

/** "4 Durchstechflaschen" → "Durchstechflaschen". Empty when the label has no unit word. */
const unitWordOf = (label: string): string => label.replace(/^[\d.,\s]+/, "").trim();

// HL-12133: Orlistat / Xenical labels count pieces ("42 St."), which read as "Monatspackung St.".
const PILL_UNIT = /^(st\.|stück|tablet|kapsel|comprim|gélule|pill|capsule)/i;

const byStrength = (a: CarePlusDosageOption, b: CarePlusDosageOption) => parseFloat(a.dosage_str) - parseFloat(b.dosage_str);

const DosageOverview = ({ t, medicationTitle, dosages, selectedProductItemId, onLearnMore, showSavingsNote, isTabletProduct, packLabel }: Props) => {
  const { formatPrice } = useCarePlusPrice();
  const rows = [...dosages].sort(byStrength);
  // A label with no unit word falls back to pens, the pre-existing default.
  const unitWord = unitWordOf(packLabel);
  const packForm =
    isTabletProduct || PILL_UNIT.test(unitWord)
      ? t("pack_form_tablets", "tablets")
      : /pen/i.test(unitWord) || !unitWord
        ? t("pack_form_pens", "injection pens")
        : unitWord;

  return (
    <div data-testid="care-plus-v3-dosage-overview" className="flex w-full flex-col items-center gap-[24px]">
      <h2 className="font-poppins text-center text-[26px] font-medium leading-[34px] text-black">
        {t("dosage_overview_title", "Price & dosage overview")}
      </h2>
      <div className="flex w-full flex-col items-center gap-[12px]">
        <p className="text-center text-[14px] leading-[1.3] text-[#616975]">
          {t(
            "dosage_overview_body",
            "By default your dose is adjusted step by step. You can change or keep it anytime in your dashboard. The price depends on your current dose. Longer treatment plans offer better value and support continuous progress.",
          )}
        </p>
        <button
          id="wl3_plan_dosage_learn_more"
          type="button"
          onClick={onLearnMore}
          className="cursor-pointer border-0 bg-transparent p-0 text-[15px] leading-[1.2] text-[#f08e00] underline"
        >
          {t("learn_more", "Learn more")}
        </button>
      </div>

      {rows.length > 0 && (
        <div className="w-full max-w-[422px] overflow-hidden rounded-[16px] border border-solid border-[#dee3ed] bg-[#fff7ea]">
          <div className="flex w-full items-end bg-[#dee3ed] py-[8px]">
            <div className="flex min-w-0 flex-1 flex-col gap-px py-[4px] pl-[24px] pr-[8px]">
              <span className="text-[10px] leading-[13px] text-[#616975]">{t("dosage_col_label", "Dosage")}</span>
              <span className="text-[14px] font-medium leading-[20px] text-black">{medicationTitle}</span>
            </div>
            <div className="flex min-w-0 flex-1 items-end py-[4px] pr-[28px]">
              <span className="whitespace-nowrap text-[14px] font-medium leading-[20px] text-black">
                {t("medication_price", "Medication price")}
              </span>
            </div>
          </div>
          {rows.map((d) => {
            const isCurrent = d.product_item_id === selectedProductItemId;
            return (
              <div
                key={d.product_item_id}
                data-testid="care-plus-v3-dosage-row"
                // border-0 first: preflight is off in this app, so border-solid alone would draw the
                // browser's 3px default on the sides that have no explicit width.
                className="flex w-full items-center border-0 border-t border-solid border-[#dee3ed] bg-[#fafcfc] py-[12px]"
              >
                <div className="flex min-w-0 flex-1 items-center gap-[4px] py-[4px] pl-[24px] pr-[8px]">
                  <span className="text-[12px] font-medium leading-[16px] text-[#616975]">{d.dosage_str}</span>
                  {/* Every row names its pack, as the Choose Treatment dropdown does: a 3-pen price
                      under the same dose as the 1-pen one otherwise reads as a second monthly price. */}
                  {rowPackLabel(d) && (
                    <span data-testid="care-plus-v3-dosage-pack" className="min-w-0 truncate text-[12px] leading-[16px] text-[#99a3b2]">
                      {rowPackLabel(d)}
                    </span>
                  )}
                  {isCurrent && <TbCheck data-testid="care-plus-v3-dosage-current" size={16} strokeWidth={2.5} className="shrink-0 text-[#f08e00]" />}
                </div>
                <div className="flex flex-1 items-center py-[4px] pr-[28px]">
                  <span className="whitespace-nowrap text-[12px] font-medium leading-[16px] text-[#616975]">
                    {formatPrice(d.price || d.official_price || 0)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p data-testid="care-plus-v3-dosage-note" className="w-full text-[12px] leading-[1.3] text-[#616975]">
        {t(
          "dosage_overview_note",
          "Every order contains a one-month pack of {{form}}. When your dose is adjusted you automatically receive the matching dosage.",
        ).replace("{{form}}", packForm)}
      </p>

      {showSavingsNote && (
      <div data-testid="care-plus-v3-savings-note" className="flex w-full items-center gap-[10px] rounded-[12px] bg-[#fff7ea] px-[12px] py-[10px]">
        {/* Figma "badge-info" (5096:105373) */}
        <svg
          aria-hidden="true"
          focusable="false"
          className="size-[24px] shrink-0"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M14.3942 3.00083L14.1481 2.79115C12.9103 1.73628 11.0897 1.73628 9.85189 2.79115L9.60584 3.00083C8.96518 3.54679 8.16862 3.87674 7.32956 3.9437L7.00731 3.96941C5.38613 4.09878 4.09878 5.38613 3.96941 7.00731L3.9437 7.32956C3.87674 8.16862 3.54679 8.96518 3.00083 9.60584L2.79115 9.85189C1.73628 11.0897 1.73628 12.9103 2.79115 14.1481L3.00083 14.3942C3.54679 15.0348 3.87674 15.8314 3.9437 16.6704L3.96941 16.9927C4.09878 18.6139 5.38613 19.9012 7.00731 20.0306L7.32956 20.0563C8.16862 20.1233 8.96518 20.4532 9.60584 20.9992L9.85188 21.2089C11.0897 22.2637 12.9103 22.2637 14.1481 21.2089L14.3942 20.9992C15.0348 20.4532 15.8314 20.1233 16.6704 20.0563L16.9927 20.0306C18.6139 19.9012 19.9012 18.6139 20.0306 16.9927L20.0563 16.6704C20.1233 15.8314 20.4532 15.0348 20.9992 14.3942L21.2089 14.1481C22.2637 12.9103 22.2637 11.0897 21.2089 9.85188L20.9992 9.60584C20.4532 8.96518 20.1233 8.16862 20.0563 7.32956L20.0306 7.00731C19.9012 5.38613 18.6139 4.09878 16.9927 3.96941L16.6704 3.9437C15.8314 3.87674 15.0348 3.54679 14.3942 3.00083Z"
            stroke="#F08E00"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M12 16V12" stroke="#F08E00" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path
            d="M12.125 8.25H12M12.25 8.25C12.25 8.11193 12.1381 8 12 8C11.8619 8 11.75 8.11193 11.75 8.25C11.75 8.38807 11.8619 8.5 12 8.5C12.1381 8.5 12.25 8.38807 12.25 8.25Z"
            stroke="#F08E00"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <p className="min-w-0 flex-1 text-[14px] leading-[1.3] text-[#f08e00]">
          {t("savings_basis_note", "The savings are based on the standard price of the 1-month plan")}
        </p>
      </div>
      )}
    </div>
  );
};

export default DosageOverview;
